/**
 * Performance Distribution Service
 * 성과 배분 API 서비스
 */

import api from "./api";
import type {
  PerformanceDistribution,
  PerformanceDistributionCreate,
  PerformanceDistributionUpdate,
  PerformanceMemberCreate,
  PerformanceAreaCreate,
  PerformanceAreaUpdate,
  PerformanceSummary,
  DepartmentOverview,
} from "@/types/performanceDistribution";

class PerformanceDistributionService {
  private basePath = "/performance-distribution";

  // 성과 배분 목록
  async getDistributions(params?: {
    project_id?: number;
  }): Promise<PerformanceDistribution[]> {
    const response = await api.get<PerformanceDistribution[]>(this.basePath, {
      params,
    });
    return response.data;
  }

  // 사업 프로젝트별 성과 배분 목록 조회
  async list(businessProjectId: number): Promise<PerformanceDistribution[]> {
    const response = await api.get<PerformanceDistribution[]>(this.basePath, {
      params: { business_project_id: businessProjectId },
    });
    return response.data;
  }

  // 배분 미생성 건수 (사이드바 배지)
  async pendingCount(): Promise<{ count: number }> {
    const response = await api.get<{ count: number }>(
      `${this.basePath}/pending-count`
    );
    return response.data;
  }

  // 성과 배분 상세
  async getDistribution(id: number): Promise<PerformanceDistribution> {
    const response = await api.get<PerformanceDistribution>(
      `${this.basePath}/${id}`
    );
    return response.data;
  }

  // 성과 배분 상세 (alias)
  async getById(id: number): Promise<PerformanceDistribution> {
    return this.getDistribution(id);
  }

  // 성과 배분 생성
  async createDistribution(
    data: PerformanceDistributionCreate
  ): Promise<PerformanceDistribution> {
    const response = await api.post<PerformanceDistribution>(
      this.basePath,
      data
    );
    return response.data;
  }

  // 성과 배분 생성 (alias)
  async create(data: any): Promise<PerformanceDistribution> {
    return this.createDistribution(data);
  }

  // 성과 배분 수정
  async updateDistribution(
    id: number,
    data: PerformanceDistributionUpdate
  ): Promise<PerformanceDistribution> {
    const response = await api.patch<PerformanceDistribution>(
      `${this.basePath}/${id}`,
      data
    );
    return response.data;
  }

  // 영역 생성 (추가지원 영역)
  async createArea(
    distributionId: number,
    data: PerformanceAreaCreate
  ): Promise<PerformanceDistribution> {
    const response = await api.post<PerformanceDistribution>(
      `${this.basePath}/${distributionId}/areas`,
      data
    );
    return response.data;
  }

  // 영역 수정
  async updateArea(
    distributionId: number,
    areaId: number,
    data: PerformanceAreaUpdate
  ): Promise<PerformanceDistribution> {
    const response = await api.patch<PerformanceDistribution>(
      `${this.basePath}/${distributionId}/areas/${areaId}`,
      data
    );
    return response.data;
  }

  // 영역 삭제 (추가지원 영역만)
  async deleteArea(
    distributionId: number,
    areaId: number
  ): Promise<PerformanceDistribution> {
    const response = await api.delete<PerformanceDistribution>(
      `${this.basePath}/${distributionId}/areas/${areaId}`
    );
    return response.data;
  }

  // 팀원 추가
  async addMember(
    distributionId: number,
    areaId: number,
    data: PerformanceMemberCreate
  ): Promise<PerformanceDistribution> {
    const response = await api.post<PerformanceDistribution>(
      `${this.basePath}/${distributionId}/areas/${areaId}/members`,
      data
    );
    return response.data;
  }

  // 팀원 삭제
  async deleteMember(
    distributionId: number,
    areaId: number,
    memberId: number
  ): Promise<void> {
    await api.delete(
      `${this.basePath}/${distributionId}/areas/${areaId}/members/${memberId}`
    );
  }

  // 개인 성과 요약
  async getMySummary(): Promise<PerformanceSummary> {
    const response = await api.get<PerformanceSummary>(
      `${this.basePath}/my/summary`
    );
    return response.data;
  }

  // 부서 수익 기여 조회
  async getMyDepartmentContribution(): Promise<PerformanceSummary> {
    const response = await api.get<PerformanceSummary>(
      `${this.basePath}/my/department-contribution`
    );
    return response.data;
  }

  // Excel 내보내기
  async exportToExcel(distributionId: number): Promise<void> {
    const response = await api.get(
      `${this.basePath}/${distributionId}/export/excel`,
      {
        responseType: 'blob',
      }
    );

    // 파일 다운로드
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;

    // 파일명 추출 (Content-Disposition 헤더에서)
    const contentDisposition = response.headers['content-disposition'];
    let filename = 'performance_distribution.xlsx';
    if (contentDisposition) {
      const filenameMatch = contentDisposition.match(/filename="?(.+)"?/);
      if (filenameMatch && filenameMatch[1]) {
        filename = filenameMatch[1];
      }
    }

    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  }

  // Excel 내보내기 (alias)
  async exportExcel(distributionId: number): Promise<void> {
    return this.exportToExcel(distributionId);
  }

  // 연동 가능한 프로젝트 목록 조회
  async getAvailableProjects(): Promise<ProjectInfo[]> {
    const response = await api.get<ProjectInfo[]>(
      `${this.basePath}/available-projects`
    );
    return response.data;
  }

  // 프로젝트 연동
  async linkProject(
    distributionId: number,
    data: ProjectLinkRequest
  ): Promise<PerformanceDistribution> {
    const response = await api.post<PerformanceDistribution>(
      `${this.basePath}/${distributionId}/link-project`,
      data
    );
    return response.data;
  }

  // 담당자 지정
  async setAreaLeader(
    distributionId: number,
    areaId: number,
    userId: string
  ): Promise<PerformanceDistribution> {
    const response = await api.patch(
      `/performance-distribution/${distributionId}/areas/${areaId}/leader?user_id=${userId}`
    );
    return response.data;
  }

  // 부서별 배분 현황 (관리자용)
  async getDepartmentOverview(): Promise<DepartmentOverview[]> {
    const response = await api.get<DepartmentOverview[]>(
      `${this.basePath}/admin/department-overview`
    );
    return response.data;
  }

  // ====================================
  // PM 전용 API (PM Management)
  // ====================================

  // PM으로 지정된 프로젝트 목록 조회
  async getMyPMProjects(): Promise<PerformanceDistribution[]> {
    const response = await api.get<PerformanceDistribution[]>(
      `${this.basePath}/my/pm-projects`
    );
    return response.data;
  }

  // PM 프로젝트 정보 수정 (PM 전용)
  async updatePMProject(
    distributionId: number,
    data: PerformanceDistributionUpdate
  ): Promise<PerformanceDistribution> {
    const response = await api.patch<PerformanceDistribution>(
      `${this.basePath}/my/pm-projects/${distributionId}`,
      data
    );
    return response.data;
  }

  // PM 프로젝트 영역 수정 (PM 전용)
  async updatePMProjectArea(
    distributionId: number,
    areaId: number,
    data: PerformanceAreaUpdate
  ): Promise<PerformanceDistribution> {
    const response = await api.patch<PerformanceDistribution>(
      `${this.basePath}/my/pm-projects/${distributionId}/areas/${areaId}`,
      data
    );
    return response.data;
  }

  // PM 프로젝트에 팀원 추가 (PM 전용)
  async addPMProjectMember(
    distributionId: number,
    areaId: number,
    data: PerformanceMemberCreate
  ): Promise<PerformanceDistribution> {
    const response = await api.post<PerformanceDistribution>(
      `${this.basePath}/my/pm-projects/${distributionId}/areas/${areaId}/members`,
      data
    );
    return response.data;
  }

  // PM 프로젝트에서 팀원 제거 (PM 전용)
  async removePMProjectMember(
    distributionId: number,
    areaId: number,
    memberId: number
  ): Promise<void> {
    await api.delete(
      `${this.basePath}/my/pm-projects/${distributionId}/areas/${areaId}/members/${memberId}`
    );
  }
}

// 타입 정의
export interface ProjectInfo {
  id: number;
  name: string;
  project_type: string;
  status: string;
}

export interface ProjectLinkRequest {
  existing_project_id?: number;
  new_project_name?: string;
  new_project_description?: string;
}

export const performanceDistributionService =
  new PerformanceDistributionService();
export default performanceDistributionService;
